import { MdClose } from 'react-icons/md';
import type { CaseStatus } from '..';

/* ─── Status Badge Styles ─── */

export const STATUS_BADGE: Record<CaseStatus, string> = {
    'Draft': 'border-[#ddd] bg-[#f5f5f5] text-[#888]',
    'New': 'border-primary/30 bg-primary/5 text-primary',
    'Assigned': 'border-green-200 bg-green-50 text-green-700',
    'In Progress': 'border-[#ddd] bg-[#f5f5f5] text-[#555]',
    'Resolved': 'border-green-200 bg-green-50 text-green-700',
    'Reopen': 'border-blue-200 bg-blue-50 text-blue-600',
};

/* ─── Helpers ─── */

export function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function formatTime(time: string) {
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
}

export const SCHEDULE_COLOR_MAP: Record<string, string> = {
    blue: 'bg-blue-600',
    orange: 'bg-primary',
    green: 'bg-green-600',
    red: 'bg-red-600',
    purple: 'bg-purple-600',
};

export function scheduleColorClass(color: string | null) {
    return SCHEDULE_COLOR_MAP[color ?? ''] ?? 'bg-blue-600';
}

/* ─── Section Card ─── */

export function SectionCard({
    icon,
    title,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
}) {
    return (
        <div className="rounded-xl border border-[#eee] p-5">
            <div className="mb-4 flex items-center gap-2 border-b border-[#eee] pb-3">
                {icon}
                <h3 className="text-[14px] font-bold text-secondary">{title}</h3>
            </div>
            {children}
        </div>
    );
}

export function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-[13px] font-semibold text-secondary">{label}</p>
            <p className="mt-1 text-[14px] text-[#555]">{value || '—'}</p>
        </div>
    );
}

/* ─── Reusable Success Panel ─── */

export function SuccessPanel({
    title,
    message,
    gradientId,
    onClose,
}: {
    title: string;
    message: string;
    gradientId: string;
    onClose: () => void;
}) {
    return (
        <div className="relative flex flex-col items-center px-8 py-12 text-center">
            <button
                type="button"
                title="Close"
                onClick={onClose}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-[#555] transition-colors hover:bg-[#f5f5f5]"
            >
                <MdClose size={20} />
            </button>

            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="36" fill={`url(#${gradientId})`} />
                {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                    <circle
                        key={angle}
                        cx={40 + 30 * Math.cos((angle * Math.PI) / 180)}
                        cy={40 + 30 * Math.sin((angle * Math.PI) / 180)}
                        r="10"
                        fill="#22c55e"
                    />
                ))}
                <circle cx="40" cy="40" r="24" fill="#22c55e" />
                <path
                    d="M28 40l8 8 16-16"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
                <defs>
                    <radialGradient id={gradientId} cx="0.5" cy="0.3" r="0.7">
                        <stop offset="0%" stopColor="#4ade80" />
                        <stop offset="100%" stopColor="#16a34a" />
                    </radialGradient>
                </defs>
            </svg>

            <h2 className="mt-6 text-xl font-bold text-secondary">{title}</h2>
            <p className="mt-4 max-w-xs text-[13px] leading-relaxed text-[#888]">{message}</p>

            <button
                type="button"
                onClick={onClose}
                className="mt-8 flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[13px] font-bold text-white transition-all hover:bg-secondary-400"
            >
                Done
            </button>
        </div>
    );
}
